import React, { useState } from "react";
import { Box, Typography, Paper, Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "../../ThemeContext";
import { getBudgetCardColors } from "./budgetPalette";
import { useFetchTeamUtilisationQuery } from "../../slice/logdetailSlice";
import {
  getBudgetNotTrackedMessage,
  getBudgetTypeLabel,
  shouldShowBudgetGauge,
} from "../../utils/budgetType";

// Palette lives in ./budgetPalette so the utilisation popup can share it.
export { getBudgetCardColors };

// Name | bar | percentage. Every row is its own grid, so the widths must be
// identical for the bars to line up. The name column is wide enough for long
// names and wraps to two lines instead of being cut off.
const TEAM_ROW_COLUMNS = {
  xs: "110px minmax(0,1fr) 54px",
  sm: "190px minmax(0,1fr) 56px",
};

export const SectionHeading = ({ children, action, colors }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3, mb: 1.25 }}>
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        bgcolor: colors.teal,
        flex: "none",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          left: "5px",
          top: "4.5px",
          width: 6,
          height: 6,
          borderRight: "2px solid #fff",
          borderBottom: "2px solid #fff",
          transform: "rotate(45deg)",
        },
      }}
    />
    <Typography sx={{ fontWeight: 500, fontSize: 15, color: colors.ink }}>
      {children}
    </Typography>
    {action}
  </Box>
);

const TeamIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <rect x="2" y="8" width="3" height="6" rx="1" fill="currentColor" />
    <rect x="6.5" y="4" width="3" height="10" rx="1" fill="currentColor" />
    <rect x="11" y="2" width="3" height="12" rx="1" fill="currentColor" />
  </svg>
);

const TeamButton = ({ onClick, colors }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-label="Show team utilisation"
    sx={{
      ml: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      fontSize: 12,
      fontWeight: 500,
      color: colors.teal,
      border: `1px solid ${colors.teal}`,
      borderRadius: "6px",
      padding: "4px 10px",
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": { background: colors.tealWash },
    }}
  >
    <TeamIcon />
    Team
  </Box>
);

/**
 * Team utilisation drawer. Data is fetched here, only while the drawer is open.
 * - Employees only ever request the hours share.
 * - Managers/Admins (allowCostToggle) can switch to cost-to-company share,
 *   which is a separate request with `?basis=cost`.
 */
const TeamUtilisationPanel = ({ open, onClose, projectId, colors, allowCostToggle }) => {
  const [requestedBasis, setRequestedBasis] = useState("hours");
  const basisToRequest = allowCostToggle ? requestedBasis : "hours";

  const { data, isFetching, isError } = useFetchTeamUtilisationQuery(
    { id: projectId, basis: basisToRequest },
    { skip: !open || !projectId }
  );

  // Trust what the server actually returned: if it ignored our basis, we
  // must not label hours numbers as cost.
  const isCost = data?.basis === "cost";
  const members = data?.members || [];
  const other = data?.other_members;
  const otherValue = typeof other?.percentage_raw === "number" ? other.percentage_raw : null;
  const maxPct =
    Math.max(...members.map((m) => m.percentage_raw || 0), otherValue || 0) || 1;
  // The insight sentence always comes from the backend (`summary` matches the
  // returned basis), it is never built on the frontend.
  const insight = data?.summary;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "50vw" },
          bgcolor: colors.paper,
          color: colors.ink,
          borderTopLeftRadius: { sm: 16 },
          borderBottomLeftRadius: { sm: 16 },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            p: "20px 22px 16px",
            borderBottom: `1px solid ${colors.line}`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: colors.ink }}>
              Team utilisation
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: colors.ink2, mt: "2px" }}>
              {isCost ? "Share of total cost-to-company" : "Share of total logged hours"}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: colors.ink2,
              border: `1px solid ${colors.line2}`,
              borderRadius: "8px",
              "&:hover": { background: colors.tealWash, color: colors.tealDeep },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: "18px 22px", overflowY: "auto", flex: 1 }}>
          {allowCostToggle && (
            <Box
              role="group"
              aria-label="Chart basis"
              sx={{
                display: "inline-flex",
                border: `1px solid ${colors.line2}`,
                borderRadius: "8px",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {[
                { key: "hours", label: "Hours" },
                { key: "cost", label: "Cost-to-company" },
              ].map((opt) => (
                <Box
                  key={opt.key}
                  component="button"
                  type="button"
                  onClick={() => setRequestedBasis(opt.key)}
                  aria-pressed={requestedBasis === opt.key}
                  sx={{
                    border: "none",
                    padding: "7px 14px",
                    fontSize: 12.5,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    fontWeight: requestedBasis === opt.key ? 600 : 400,
                    background: requestedBasis === opt.key ? colors.teal : "transparent",
                    color: requestedBasis === opt.key ? "#fff" : colors.ink,
                  }}
                >
                  {opt.label}
                </Box>
              ))}
            </Box>
          )}

          {isError && (
            <Typography sx={{ fontSize: 12.5, color: colors.red, mb: 2 }}>
              Could not load team utilisation.
            </Typography>
          )}

          {insight && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                background: colors.tealWash,
                color: colors.tealDeep,
                borderRadius: "10px",
                padding: "10px 12px",
                mb: 2,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" style={{ flex: "none", marginTop: 2 }}>
                <path d="M8 1.5l1.6 4.3 4.4 1.7-4.4 1.7L8 13.5 6.4 9.2 2 7.5l4.4-1.7z" fill="currentColor" />
              </svg>
              <span>{insight}</span>
            </Box>
          )}

          <Box
            sx={{
              border: `1px solid ${colors.line}`,
              borderRadius: "10px",
              p: "6px 14px",
              opacity: isFetching ? 0.6 : 1,
            }}
          >
            {!isFetching && members.length === 0 && (
              <Typography sx={{ fontSize: 12.5, color: colors.ink3, py: 1.5, textAlign: "center" }}>
                No logged hours yet.
              </Typography>
            )}
            {isFetching && members.length === 0 && (
              <Typography sx={{ fontSize: 12.5, color: colors.ink3, py: 1.5, textAlign: "center" }}>
                Loading…
              </Typography>
            )}
            {members.map((m, i) => {
              const isMe = !!m.is_current_user;
              const value = m.percentage_raw || 0;
              const width = Math.max((value / maxPct) * 100, 2).toFixed(1);
              // The API already appends "(You)" to the current user's name.
              const showYouTag = isMe && !/\(you\)/i.test(m.name || "");
              return (
                <Box
                  key={m.user_id ?? m.name}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: TEAM_ROW_COLUMNS,
                    alignItems: "center",
                    gap: 1.25,
                    py: 1,
                    fontSize: 12.5,
                    borderBottom:
                      i === members.length - 1 ? "none" : `1px solid ${colors.line}`,
                    background: isMe ? colors.tealWash : "transparent",
                    borderRadius: isMe ? "6px" : 0,
                    px: isMe ? 1 : 0,
                    mx: isMe ? "-8px" : 0,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="span"
                      title={m.name}
                      sx={{
                        fontWeight: isMe ? 600 : 400,
                        fontSize: 12.5,
                        lineHeight: 1.3,
                        color: isMe ? colors.tealDeep : colors.ink,
                        // wrap up to 2 lines, then ellipsis (full name on hover)
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {m.name}
                    </Typography>
                    <Typography sx={{ fontSize: "10.5px", color: isMe ? colors.teal : colors.ink3 }}>
                      {m.hours || "—"}
                      {showYouTag ? " · You" : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 10, background: colors.barBg, borderRadius: "5px", overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        borderRadius: "5px",
                        width: `${width}%`,
                        background: isMe ? colors.tealDeep : colors.teal,
                        opacity: isMe ? 1 : 0.75,
                      }}
                    />
                  </Box>
                  <Typography sx={{ textAlign: "right", fontWeight: isMe ? 600 : 400, fontSize: 12.5, color: colors.ink }}>
                    {value.toFixed(2)}%
                  </Typography>
                </Box>
              );
            })}

            {otherValue !== null && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: TEAM_ROW_COLUMNS,
                  alignItems: "center",
                  gap: 1.25,
                  py: 1,
                  fontSize: 12.5,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 12.5, color: colors.ink3 }}>
                    {other.label || "Other members"}
                    {other.count ? ` (${other.count})` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10.5px", color: colors.ink3 }}>{other.hours || "—"}</Typography>
                </Box>
                <Box sx={{ height: 10, background: colors.barBg, borderRadius: "5px", overflow: "hidden" }}>
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: "5px",
                      width: `${Math.max((otherValue / maxPct) * 100, 2).toFixed(1)}%`,
                      background: colors.line2,
                    }}
                  />
                </Box>
                <Typography sx={{ textAlign: "right", fontSize: 12.5, color: colors.ink }}>
                  {otherValue.toFixed(2)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ p: "12px 22px 18px", borderTop: `1px solid ${colors.line}` }}>
          <Typography sx={{ fontSize: 11.5, color: colors.ink3 }}>
            {allowCostToggle
              ? "Switch between hours and cost-to-company share above."
              : "Showing your share of total logged hours."}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

// Shown instead of the gauge for projects without a fixed budget (Time & Material,
// Contract): a percentage-of-budget meter would be meaningless there.
const BudgetNotTracked = ({ budgetType, colors }) => {
  const typeLabel = getBudgetTypeLabel(budgetType);

  return (
    <Paper
      role="status"
      data-testid="budget-not-tracked"
      variant="outlined"
      sx={{
        border: `1px dashed ${colors.line2}`,
        borderRadius: "10px",
        p: 2.25,
        bgcolor: colors.head,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.75,
      }}
    >
      <Box
        sx={{
          flex: "none",
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: colors.tealWash,
          color: colors.tealDeep,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <InfoOutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: colors.ink }}>
            Budget progress isn&apos;t tracked for this project
          </Typography>
          {typeLabel && (
            <Box
              component="span"
              sx={{
                fontSize: 11.5,
                fontWeight: 500,
                padding: "2px 9px",
                borderRadius: "999px",
                border: `1px solid ${colors.line2}`,
                color: colors.ink2,
                bgcolor: colors.paper,
              }}
            >
              {typeLabel}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, color: colors.ink2, mt: 0.5, lineHeight: 1.5 }}>
          {getBudgetNotTrackedMessage(budgetType)}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: colors.ink3, mt: 0.75 }}>
          Budget used is shown for Fixed Cost projects only.
        </Typography>
      </Box>
    </Paper>
  );
};

// Neutral placeholder while the project type loads, so the card never flashes the
// gauge and then swaps it for the message.
const BudgetStatusPlaceholder = ({ colors }) => (
  <Paper
    variant="outlined"
    aria-busy="true"
    data-testid="budget-status-loading"
    sx={{ border: `1px solid ${colors.line2}`, borderRadius: "10px", p: 2, bgcolor: colors.paper }}
  >
    <Box sx={{ height: 14, width: "30%", borderRadius: "4px", bgcolor: colors.barBg }} />
    <Box sx={{ height: 28, width: "20%", borderRadius: "4px", bgcolor: colors.barBg, my: 1.5 }} />
    <Box sx={{ height: 8, borderRadius: "4px", bgcolor: colors.barBg }} />
  </Paper>
);

/**
 * "Budget status" gauge card, shared by the employee and manager/admin
 * budget views. Renders a 0–150% meter with a 100% marker and opens the
 * Team utilisation drawer.
 *
 * @param {string|number} projectId - project whose team utilisation is loaded.
 * @param {boolean} allowCostToggle - MANAGER/ADMIN only: shows the Hours vs
 *   Cost-to-company toggle in the Team drawer. Employees never send a cost
 *   request.
 * @param {string|null} budgetType - the project's `budget_type` (used to name the
 *   type in the explanation).
 * @param {boolean} [budgetTracked] - whether the gauge applies, as reported by the
 *   budget overview (`budget_tracked`). When omitted it is worked out from
 *   `budgetType`: the gauge is only shown for FIXED_COST (or when the type was
 *   never set); Time & Material and Contract projects get an explanation instead
 *   of a meaningless percentage.
 * @param {boolean} budgetTypeLoading - true while the project (and so its type)
 *   is still loading.
 */
const BudgetStatusCard = ({
  usedPct = 0,
  usedDisplay,
  status = "—",
  isOver = false,
  gaugeMax = 150,
  gaugeMarker = 100,
  projectId,
  allowCostToggle = false,
  budgetType = null,
  budgetTracked,
  budgetTypeLoading = false,
}) => {
  const [teamOpen, setTeamOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const colors = getBudgetCardColors(isDarkMode);

  const meterWidth = Math.min((usedPct / gaugeMax) * 100, 100);
  const markerPos = `${((gaugeMarker / gaugeMax) * 100).toFixed(2)}%`;
  const meterColor = isOver ? colors.red : colors.teal;

  return (
    <>
      <SectionHeading colors={colors} action={<TeamButton colors={colors} onClick={() => setTeamOpen(true)} />}>
        Budget status
      </SectionHeading>
      {budgetTypeLoading ? (
        <BudgetStatusPlaceholder colors={colors} />
      ) : !(typeof budgetTracked === "boolean" ? budgetTracked : shouldShowBudgetGauge(budgetType)) ? (
        <BudgetNotTracked budgetType={budgetType} colors={colors} />
      ) : (
      <Paper
        variant="outlined"
        sx={{ border: `1px solid ${colors.line2}`, borderRadius: "10px", p: 2, bgcolor: colors.paper }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: colors.ink2, fontSize: 14 }}>
          <span>Budget used</span>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              padding: "3px 10px",
              borderRadius: "6px",
              background: isOver ? colors.redWash : colors.tealWash,
              color: isOver ? colors.red : colors.tealDeep,
            }}
          >
            {status}
          </Box>
        </Box>
        <Typography sx={{ fontSize: 28, fontWeight: 500, my: 1, color: colors.ink }}>
          {usedDisplay || `${usedPct}%`}
        </Typography>
        <Box sx={{ position: "relative", height: 8, background: colors.barBg, borderRadius: "4px", overflow: "hidden" }}>
          <Box sx={{ height: "100%", width: `${meterWidth}%`, background: meterColor }} />
          <Box
            sx={{
              position: "absolute",
              left: markerPos,
              top: 0,
              width: "2px",
              height: "100%",
              background: colors.ink2,
            }}
          />
        </Box>
        <Box sx={{ position: "relative", height: 16, fontSize: 11, color: colors.ink3, mt: 0.5 }}>
          <span style={{ position: "absolute", left: 0 }}>0%</span>
          <span style={{ position: "absolute", left: markerPos, transform: "translateX(-50%)" }}>{gaugeMarker}%</span>
          <span style={{ position: "absolute", right: 0 }}>{gaugeMax}%</span>
        </Box>
      </Paper>
      )}

      <TeamUtilisationPanel
        open={teamOpen}
        onClose={() => setTeamOpen(false)}
        projectId={projectId}
        colors={colors}
        allowCostToggle={allowCostToggle}
      />
    </>
  );
};

export default BudgetStatusCard;
